/**
 * EKO — native free gift: cart auto-add (snippets/eko-free-gift.liquid)
 *
 * Replaces the Bogos free-gift app's cart behaviour. The gift's PRICE comes
 * from a native automatic Buy X Get Y discount; this file only keeps exactly
 * one gift line in the cart while the qualifying subtotal is at or above the
 * threshold, and takes the auto-added line out when it drops below.
 *
 * Qualifying subtotal: sum of `final_line_price` of every line that is not the
 * gift variant and carries no product discount (empty
 * `line_level_discount_allocations`). Shopify's Buy X Get Y "minimum purchase
 * amount" counts items at full price and leaves out any item that already has
 * a combinable product discount (Shopify Help Center, checked 2026-10-12), so
 * the theme never adds a gift the discount would then charge for. Order-level
 * codes are not line allocations and don't change the sum.
 * snippets/eko-gift-progress.liquid computes the same number in Liquid.
 *
 * When it runs
 *   - once on load (only if the cart is non-empty),
 *   - after every `shopify:cart:lines-update` / `shopify:cart:discount-update`
 *     that someone else dispatched has settled (debounced),
 *   - on bfcache restore, and when "Add my free mitt" is pressed.
 *
 * Loop / race guards
 *   - debounce (350 ms) and ONE request chain in flight; triggers that arrive
 *     meanwhile collapse into a single re-run afterwards;
 *   - every decision re-reads /cart.js first, never trusts a cached cart;
 *   - our own events are dispatched from `host` and ignored by our listener;
 *   - a rolling budget of 6 cart mutations per minute; past it we stop and warn;
 *   - a failed add (sold out, unknown variant) is not retried for 10 minutes.
 *
 * Promotional-only gift (cfg.promoOnly, Theme settings "Gift is promotional
 * only"): every line of the gift variant is managed, not just the auto-added
 * one — below the threshold all are removed, at or above it one line of
 * quantity 1 is kept (the auto-added line if there is one). Without it, a mitt
 * the shopper added themselves is never touched.
 *
 * Shopper choice: if the gift disappears while the subtotal is still above the
 * threshold (the shopper removed it), it is not re-added in this browser
 * session until the subtotal falls below and crosses the threshold again
 * (sessionStorage). The drawer's progress bar then shows an "Add my free mitt"
 * button ([data-eko-gift-add]) that clears that choice.
 *
 * Refresh: requests the Section Rendering API HTML of every
 * <cart-items-component> with the mutation (same as product-form.js) and
 * dispatches a CartLinesUpdateEvent with action 'update' / 'remove' so the
 * drawer, cart page, cart bubble and the header's count announcement update
 * through the theme's own listeners. 'add' is deliberately not used: it would
 * auto-open a closed drawer on page load.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const STORE_KEY = 'eko-free-gift:v1';
const GIFT_PROP = '_eko_gift';
const DEBOUNCE_MS = 350;
const BUDGET = { max: 6, windowMs: 60000 };
const RETRY_AFTER_FAIL_MS = 10 * 60 * 1000;
const LOG = '[eko-free-gift]';

/** @returns {{variantId:number, threshold:number, promoOnly?:boolean, added:string, removed:string} | null} */
function readConfig() {
  const el = document.getElementById('eko-free-gift-config');
  if (!el) return null;
  try {
    const cfg = JSON.parse(el.textContent || '{}');
    if (!(cfg.variantId > 0) || !(cfg.threshold > 0)) return null;
    return cfg;
  } catch (e) {
    return null;
  }
}

/** sessionStorage can throw (privacy modes); the feature must still work. */
const store = {
  read() {
    try {
      return { declined: false, sawGift: false, failedAt: 0, failedVariant: 0, ...JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}') };
    } catch (e) {
      return { declined: false, sawGift: false, failedAt: 0, failedVariant: 0 };
    }
  },
  write(state) {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) {
      /* ignore */
    }
  },
};

const routes = () => {
  const r = (typeof Theme !== 'undefined' && Theme.routes) || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return {
    add: r.cart_add_url || `${root}cart/add.js`,
    change: `${r.cart_change_url || `${root}cart/change`}.js`,
    cart: `${r.cart_url || `${root}cart`}.js`,
  };
};

class EkoFreeGift {
  constructor(cfg) {
    this.cfg = cfg;
    this.timer = 0;
    this.inFlight = false;
    this.again = false;
    /** @type {number[]} */
    this.mutations = [];

    // Event source + page-level polite live region (used when no modal drawer is open).
    this.host = document.createElement('div');
    this.host.className = 'visually-hidden';
    this.host.setAttribute('data-eko-free-gift', '');
    this.live = document.createElement('div');
    this.live.setAttribute('role', 'status');
    this.live.setAttribute('aria-live', 'polite');
    this.live.setAttribute('aria-atomic', 'true');
    this.host.appendChild(this.live);
    document.body.appendChild(this.host);

    const onCartEvent = (event) => {
      if (event.target === this.host) return;
      const p = event.promise;
      if (p && typeof p.then === 'function') {
        p.then(
          () => this.schedule(),
          () => this.schedule()
        );
      } else {
        this.schedule();
      }
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, onCartEvent);
    document.addEventListener(StandardEvents.cartDiscountUpdate, onCartEvent);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.schedule();
    });
    document.addEventListener('click', (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-eko-gift-add]') : null;
      if (!btn) return;
      event.preventDefault();
      const state = store.read();
      state.declined = false;
      state.failedAt = 0;
      store.write(state);
      btn.setAttribute('aria-disabled', 'true');
      this.schedule(0);
    });
  }

  schedule(delay = DEBOUNCE_MS) {
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.run(), delay);
  }

  async run() {
    if (this.inFlight) {
      this.again = true;
      return;
    }
    this.inFlight = true;
    try {
      const cart = await this.getCart();
      if (cart) await this.reconcile(cart);
    } catch (error) {
      console.warn(LOG, error);
    } finally {
      this.inFlight = false;
      if (this.again) {
        this.again = false;
        this.schedule();
      }
    }
  }

  async getCart() {
    const res = await fetch(routes().cart, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) return null;
    return res.json();
  }

  /** @param {any} cart */
  async reconcile(cart) {
    const { variantId, threshold } = this.cfg;
    const items = Array.isArray(cart.items) ? cart.items : [];
    const giftLines = items.filter((i) => i.variant_id === variantId);
    const isAuto = (i) => !!(i.properties && i.properties[GIFT_PROP]);
    // Promotional-only: every gift line is ours to manage; the auto-added line sorts first so it is the one kept.
    const autoLines = this.cfg.promoOnly
      ? [...giftLines.filter(isAuto), ...giftLines.filter((i) => !isAuto(i))]
      : giftLines.filter(isAuto);
    const qualifying = items
      .filter((i) => i.variant_id !== variantId)
      .filter((i) => !(Array.isArray(i.line_level_discount_allocations) && i.line_level_discount_allocations.length))
      .reduce((sum, i) => sum + (Number(i.final_line_price) || 0), 0);
    const above = qualifying >= threshold;
    const state = store.read();

    if (!above) {
      // Below (or crossed back below): the next crossing is a fresh offer.
      state.declined = false;
      state.sawGift = false;
      store.write(state);
      this.syncButton(false);
      if (autoLines.length) {
        let removed = false;
        for (const line of autoLines) {
          if (await this.change(line.key, 0, 'remove')) removed = true;
        }
        if (removed) this.announce(this.cfg.removed);
      }
      return;
    }

    if (giftLines.length) {
      state.sawGift = true;
      state.declined = false;
      store.write(state);
      this.syncButton(false);
      if (autoLines.length > 1) {
        // Two auto lines come from a race in another tab (or, promotional-only, a second
        // route into the cart): keep the first, drop the rest.
        for (const line of autoLines.slice(1)) await this.change(line.key, 0, 'remove');
      }
      if (autoLines.length && autoLines[0].quantity > 1) {
        await this.change(autoLines[0].key, 1, 'update');
      }
      return;
    }

    // Above the threshold with no gift line.
    if (state.sawGift) {
      // We saw the gift in this cart and nobody below-threshold removed it: the shopper did.
      state.sawGift = false;
      state.declined = true;
      store.write(state);
    }
    if (state.declined) {
      this.syncButton(true);
      return;
    }
    if (state.failedVariant === variantId && Date.now() - state.failedAt < RETRY_AFTER_FAIL_MS) {
      this.syncButton(false);
      return;
    }
    await this.add();
  }

  sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return [...ids];
  }

  spend() {
    const now = Date.now();
    this.mutations = this.mutations.filter((t) => now - t < BUDGET.windowMs);
    if (this.mutations.length >= BUDGET.max) {
      console.warn(LOG, 'mutation budget reached; pausing auto-add for this minute');
      return false;
    }
    this.mutations.push(now);
    return true;
  }

  async add() {
    if (!this.spend()) return false;
    const { variantId } = this.cfg;
    const sections = this.sectionIds();
    const body = { items: [{ id: variantId, quantity: 1, properties: { [GIFT_PROP]: '1' } }] };
    if (sections.length) body.sections = sections.join(',');

    let data = null;
    let ok = false;
    try {
      const res = await fetch(routes().add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      data = await res.json().catch(() => null);
      ok = res.ok && data && !data.status;
    } catch (error) {
      ok = false;
    }

    const state = store.read();
    if (!ok) {
      state.failedVariant = variantId;
      state.failedAt = Date.now();
      store.write(state);
      console.warn(LOG, 'gift could not be added:', (data && (data.description || data.message)) || 'network error');
      return false;
    }

    const cart = await this.getCart();
    const line = cart && cart.items.find((i) => i.variant_id === variantId && i.properties && i.properties[GIFT_PROP]);
    state.sawGift = true;
    state.declined = false;
    state.failedAt = 0;
    store.write(state);
    if (cart && line) this.dispatch('update', line.key, 1, cart, data.sections);
    this.syncButton(false);
    this.announce(this.cfg.added);
    return true;
  }

  /**
   * @param {string} key  cart line key
   * @param {number} quantity
   * @param {'update' | 'remove'} action
   */
  async change(key, quantity, action) {
    if (!this.spend()) return false;
    const sections = this.sectionIds();
    const body = { id: key, quantity };
    if (sections.length) body.sections = sections.join(',');
    try {
      const res = await fetch(routes().change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const cart = await res.json().catch(() => null);
      if (!res.ok || !cart || cart.status) {
        console.warn(LOG, 'gift line change failed:', cart && (cart.description || cart.message));
        return false;
      }
      this.dispatch(action, key, quantity, cart, cart.sections);
      return true;
    } catch (error) {
      console.warn(LOG, error);
      return false;
    }
  }

  /**
   * Lets the theme's own listeners morph the drawer / cart page and update the bubble.
   * @param {'update' | 'remove'} action
   * @param {string} key
   * @param {number} quantity
   * @param {any} cart  AJAX cart (cart.js or change.js response)
   * @param {Record<string, string> | undefined} sections
   */
  dispatch(action, key, quantity, cart, sections) {
    const deferred = CartLinesUpdateEvent.createPromise();
    this.host.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [{ id: key, quantity }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: sections || undefined,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'eko-free-gift',
        didError: false,
      },
    });
  }

  /** Reveal the drawer's "Add my free mitt" button only after the shopper declined the gift. */
  syncButton(show) {
    document.querySelectorAll('[data-eko-gift-add]').forEach((btn) => {
      btn.hidden = !show;
      btn.removeAttribute('aria-disabled');
    });
  }

  /**
   * Polite announcement. A modal cart drawer makes the rest of the page inert, so
   * while it is open the message goes to a region inside the dialog.
   * @param {string} message
   */
  announce(message) {
    if (!message) return;
    const dialog = document.querySelector('#cart-drawer dialog[open]');
    let region = this.live;
    let fresh = false;
    if (dialog) {
      region = dialog.querySelector(':scope > [data-eko-free-gift-live]');
      if (!region) {
        region = document.createElement('div');
        region.className = 'visually-hidden';
        region.setAttribute('data-eko-free-gift-live', '');
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'true');
        dialog.appendChild(region);
        fresh = true;
      }
    }
    const target = region;
    target.textContent = '';
    // A live region must exist (and be empty) before its text changes to be announced reliably;
    // the delay also lets the header's own "Cart count" announcement go first.
    window.setTimeout(() => {
      target.textContent = message;
    }, fresh ? 400 : 250);
    window.setTimeout(() => {
      if (target.textContent === message) target.textContent = '';
    }, 8000);
  }
}

function init() {
  const cfg = readConfig();
  if (!cfg || window.__ekoFreeGift) return;
  window.__ekoFreeGift = new EkoFreeGift(cfg);
  const count = Number(document.getElementById('eko-free-gift-config')?.dataset.itemCount ?? 1);
  if (count > 0) window.__ekoFreeGift.schedule(0);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
